import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Menu, Globe } from 'lucide-react';
import { LANGS, t } from '../i18n';
import { clearUserAuth, getUser, getDoctor } from '../lib/auth';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { api } from '../lib/api';

export default function Navbar({ lang, setLang }) {
  const loc = useLocation();
  const user = getUser();
  const doctor = getDoctor();
  const logout = async () => {
    clearUserAuth();
    try {
      await api.post('/auth/logout');
    } catch {
      // Local auth is already cleared; redirect either way.
    }
    window.location.href = '/login';
  };
  const linkCls = (path) =>
    `px-4 py-2 rounded-full text-[15px] font-semibold transition-colors ${
      loc.pathname === path ? 'bg-[#1E3F33] text-white' : 'text-[#1A1A1A] hover:bg-[#F4F1EA]'
    }`;
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAFAFA]/80 border-b border-[#E8E1D5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <div className="h-11 w-11 rounded-2xl bg-[#1E3F33] grid place-items-center">
            <Heart className="h-6 w-6 text-[#E07A5F]" fill="#E07A5F" />
          </div>
          <div>
            <div className="font-display text-2xl leading-none text-[#1A1A1A]">Health Buddy</div>
            <div className="text-xs text-[#7A7A7A] mt-0.5 tracking-wide">care in your language</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-1" data-testid="nav-links">
          <Link to="/" className={linkCls('/')} data-testid="nav-home">{t(lang,'nav_home')}</Link>
          
          {doctor ? (
            <>
              <Link to="/doctor/dashboard" className={linkCls('/doctor/dashboard')} data-testid="nav-doctor-dashboard">Doctor Portal</Link>
              <button
                onClick={logout}
                className="px-4 py-2 rounded-full text-[15px] font-semibold text-[#1A1A1A] hover:bg-[#F4F1EA]"
                data-testid="nav-user-logout"
              >
                {t(lang,'nav_logout')}
              </button>
            </>
          ) : user ? (
            <>
              <Link to="/chat" className={linkCls('/chat')} data-testid="nav-chat">Talk with Buddy</Link>
              <Link to="/dashboard" className={linkCls('/dashboard')} data-testid="nav-dashboard">{t(lang,'nav_dashboard')}</Link>
              <Link to="/health-hub" className={linkCls('/health-hub')} data-testid="nav-health-hub">Health Hub</Link>
              <Link to="/consultations" className={linkCls('/consultations')} data-testid="nav-consultations">{t(lang,'nav_consultations')}</Link>
              <Link to="/history" className={linkCls('/history')} data-testid="nav-history">My Reports</Link>
              <button
                onClick={logout}
                className="px-4 py-2 rounded-full text-[15px] font-semibold text-[#1A1A1A] hover:bg-[#F4F1EA]"
                data-testid="nav-user-logout"
              >
                {t(lang,'nav_logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/chat" className={linkCls('/chat')} data-testid="nav-chat">Talk with Buddy</Link>
              <Link to="/login" className={linkCls('/login')} data-testid="nav-user-login">Login / Signup</Link>
            </>
          )}
        </nav>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full h-11 border-[#1E3F33] text-[#1E3F33] font-semibold" data-testid="lang-dropdown">
                <Globe className="h-4 w-4 mr-2" /> {LANGS.find(l=>l.code===lang)?.short}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} onClick={()=>setLang(l.code)} data-testid={`lang-option-${l.code}`} className="text-base py-2.5">
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {!doctor && (
            <Link to="/chat" className="hidden sm:inline">
              <Button className="rounded-full h-11 px-5 bg-[#E07A5F] hover:bg-[#C9674D] text-white font-semibold" data-testid="nav-cta-chat">
                Talk with Buddy
              </Button>
            </Link>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="nav-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              <DropdownMenuItem asChild><Link to="/" data-testid="mnav-home">{t(lang,'nav_home')}</Link></DropdownMenuItem>
              
              {doctor ? (
                <>
                  <DropdownMenuItem asChild><Link to="/doctor/dashboard" data-testid="mnav-doctor-dashboard">Doctor Portal</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="mnav-logout">
                    {t(lang,'nav_logout')}
                  </DropdownMenuItem>
                </>
              ) : user ? (
                <>
                  <DropdownMenuItem asChild><Link to="/chat" data-testid="mnav-chat">Talk with Buddy</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/dashboard" data-testid="mnav-dashboard">{t(lang,'nav_dashboard')}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/health-hub" data-testid="mnav-health-hub">Health Hub</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/consultations" data-testid="mnav-consultations">{t(lang,'nav_consultations')}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/history" data-testid="mnav-history">My Reports</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="mnav-logout">
                    {t(lang,'nav_logout')}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild><Link to="/chat" data-testid="mnav-chat">Talk with Buddy</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/login" data-testid="mnav-login">Login / Signup</Link></DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem asChild><Link to="/privacy" data-testid="mnav-privacy">Privacy Policy</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/terms" data-testid="mnav-terms">Terms of Service</Link></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
